import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, increment } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import {
  FaMapMarkerAlt, FaMedal, FaStar, FaUserFriends,
  FaBed, FaCheckCircle, FaFilter, FaEye, FaIdBadge
} from 'react-icons/fa';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { db, storage } from './firebase';
import './PGListing.css';
import apnagharLogo from './apnaghar.png';
import LoadingScreen from './LoadingScreen';

// Cache
const ratingCache = {};

const PGListView = () => {
  const { type } = useParams();
  const [pgList, setPgList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('rent');
  const [occupancyFilter, setOccupancyFilter] = useState('');
  const navigate = useNavigate();
  const isMounted = useRef(true);
  const scrollRestored = useRef(false);

  // ---------------- Fetch PGs (No API Calls) ----------------
  useEffect(() => {
    isMounted.current = true;

    const fetchPGs = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'house-listings'));
        let data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          thumbnailURL: null,
          views: doc.data().views || 0,
          showBooked: doc.data().showBooked || false,
          location: doc.data().location || 'Assam',
          road: doc.data().road || [],
          ratingAvg: doc.data().ratingAvg || 0,
          state: doc.data().state || 'Assam', 
          ratingCount: doc.data().ratingCount || 0
        }));

        // Filter by type
        if (type === 'featured') data = data.filter(pg => pg.isPremium);
        else if (type === 'verified') data = data.filter(pg => pg.isVerified && !pg.isPremium);

        if (!isMounted.current) return;
        setPgList(data);
        setLoading(false);

        // Fetch thumbnail from Firebase storage
        data.forEach(async (pg, i) => {
          const updates = {};
          if (pg.thumbnailPath) {
            try {
              updates.thumbnailURL = await getDownloadURL(ref(storage, pg.thumbnailPath));
            } catch (err) {
              console.warn(`Error fetching thumbnail for ${pg.id}`, err);
            }
          }

          if (Object.keys(updates).length && isMounted.current) {
            setPgList(prev => {
              const copy = [...prev];
              copy[i] = { ...copy[i], ...updates };
              return copy;
            });
          }

          if (!ratingCache[pg.id]) {
            ratingCache[pg.id] = { avg: pg.ratingAvg, count: pg.ratingCount };
          }
        });
      } catch (err) {
        console.error('Error fetching PGs:', err);
        if (isMounted.current) setLoading(false);
      }
    };

    fetchPGs();
    return () => { isMounted.current = false; };
  }, [type]);

  // ---------------- Restore scroll ----------------
  useEffect(() => {
    if (!loading && !scrollRestored.current) {
      const savedCardId = sessionStorage.getItem(`pg-card-${type}`);
      const savedScroll = sessionStorage.getItem(`pg-scroll-${type}`);

      const restoreScroll = () => {
        if (savedCardId) {
          const el = document.getElementById(`pg-card-${savedCardId}`);
          if (el) {
            el.scrollIntoView({ behavior: 'auto', block: 'center' });
            sessionStorage.removeItem(`pg-card-${type}`);
          } else {
            requestAnimationFrame(restoreScroll);
            return;
          }
        } else if (savedScroll) {
          requestAnimationFrame(() => {
            window.scrollTo({ top: parseInt(savedScroll, 10), behavior: 'auto' });
          });
        }
        scrollRestored.current = true;
      };

      restoreScroll();
    }
  }, [loading, type, pgList]);

  // ---------------- Save scroll position ----------------
  useEffect(() => {
    const handleScroll = () => {
      sessionStorage.setItem(`pg-scroll-${type}`, window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [type]);

  // ---------------- Handle Card Click ----------------
  const handleCardClick = useCallback(
    async (pg) => {
      if (pg.showBooked) return;
      sessionStorage.setItem(`pg-card-${type}`, pg.id);

      try {
        await updateDoc(doc(db, 'house-listings', pg.id), { views: increment(1) });
        setPgList(prev =>
          prev.map(p => (p.id === pg.id ? { ...p, views: (p.views || 0) + 1 } : p))
        );
      } catch (err) { console.error(err); }

      navigate(`/pg/${pg.id}`);
    },
    [navigate, type]
  );

  // ---------------- Filtering & Sorting ----------------
  const filteredPGs = pgList.filter(pg => {
    const matchesSearch = !search || (pg[filterType] ?? '').toString().toLowerCase().includes(search.toLowerCase());
    const matchesOccupancy = !occupancyFilter || (pg.occupancy?.toLowerCase() === occupancyFilter.toLowerCase());
    return matchesSearch && matchesOccupancy;
  });

  const sortedPGs = [...filteredPGs].sort((a, b) => {
    if (filterType === "rent") return (a.rent || 0) - (b.rent || 0);
    if (filterType === "ratingAvg") return (b.ratingAvg || 0) - (a.ratingAvg || 0);
    if (filterType === "views") return (b.views || 0) - (a.views || 0);
    return 0;
  });

  if (loading) return <LoadingScreen />;

  // ---------------- UI ----------------
  return (
    <div className="pg-section">
      <button onClick={() => navigate(-1)} className="back-btnwhy">← Back</button>
      <div className="pg-container">

        <div className="logoo-container" style={{ textAlign: 'center', marginBottom: '20px' }}>
          <img src={apnagharLogo} alt="ApkaGhr Logo" style={{ maxWidth: '180px', height: 'auto' }} />
          <h1
            className="brand-text"
            style={{
              background: 'linear-gradient(90deg, #E60056, #4126A7)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            ApkaGhr
          </h1>
        </div>

        <h2 className="section-header-title">
          {type === 'featured' ? 'Premium Listings' : type === 'verified' ? 'Verified Listings' : 'All Listings'}
        </h2>

        <div className="filter-bar">
          <div className="filter-group">
            <FaFilter className="filter-icon" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              <option value="rent">Sort by Rent</option>
              <option value="ratingAvg">Sort by Rating</option>
              <option value="views">Sort by Views</option>
            </select>
          </div>

          <div className="filter-group">
            <FaUserFriends className="filter-icon" />
            <select
              value={occupancyFilter}
              onChange={(e) => setOccupancyFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Occupancy</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="coliving">Coliving</option>
            </select>
          </div>

          <button
            className="reset-btn"
            onClick={() => {
              setFilterType('rent');
              setOccupancyFilter('');
            }}
          >
            Reset Filters
          </button>
        </div>

        <div className="pg-grid">
          {filteredPGs.length === 0 ? (
            <p>No listings found.</p>
          ) : (
            sortedPGs.map(pg => {
              const rating = ratingCache[pg.id] || { avg: 0, count: 0 };
              return (
                <div
                  key={pg.id}
                  id={`pg-card-${pg.id}`}
                  className={`pg-card-wrapper ${pg.showBooked ? 'booked' : ''}`}
                  onClick={() => handleCardClick(pg)}
                >
                  {pg.showBooked && (
                    <div className="booked-overlay">
                      <div className="booked-text">🏠 Booked</div>
                      <p className="booked-subtext">No rooms available</p>
                    </div>
                  )}

                  {pg.isPremium && <div className="premium-badge"><FaMedal /> Featured</div>}

                  <div className={`pg-card ${pg.isPremium ? 'premium-card' : ''} ${pg.isVerified && !pg.isPremium ? 'verified-border' : ''}`}>
                    <LazyLoadImage
                      src={pg.thumbnailURL || 'https://dummyimage.com/400x300/cccccc/000000&text=No+Image'}
                      alt={pg.pgName || 'PG Image'}
                      effect="blur"
                      className="pg-img"
                    />
                    <div className="pg-content">
                      <h3>{pg.pgName} {pg.isVerified && <FaCheckCircle className="verified-tick" />}</h3>
                  <p>
  <FaMapMarkerAlt /> 
  {pg.city || pg.location || 'City'} , {pg.state || 'State'}
</p>


                      <p><FaUserFriends /> {pg.occupancy || 'N/A'}</p>
                      <p><FaBed /> {pg.roomType || 'N/A'}</p>
                      <p className="price">₹ {pg.rent || 'N/A'}/month</p>
                      <p><FaIdBadge /> Listing ID: {pg.id}</p>

                      {pg.road && Array.isArray(pg.road) && pg.road.length > 0 && (
                        <div className="road-slider">
                          {pg.road.map((r, idx) => (
                            <div key={idx} className="road-info">
                              <FaMapMarkerAlt className="road-icon" />
                              <span>{r}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <p className="rating">
                        {[1, 2, 3, 4, 5].map(n => (
                          <FaStar key={n} color={n <= Math.round(rating.avg) ? '#f5c518' : '#d0d0d0'} />
                        ))} {rating.count > 0 && <span>({rating.count})</span>}
                      </p>
                      <p className="views"><FaEye /> {pg.views} views</p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default PGListView;
